import {
  Wallet, FilePlus2, Printer, UserPlus, House, CalendarCheck, UserCheck, UserRoundPlus, Banknote,
  ShoppingCart, PackagePlus, ClipboardList, PackageCheck, Truck, Receipt, Landmark, GraduationCap,
  CalendarClock, BookOpen, MessageSquareWarning, BarChart3, ShieldCheck, Settings as SettingsIcon,
} from 'lucide-react';

// One place that knows where things live: the page id from App.jsx, the sub-tab a page
// understands (see usePageTab), and the permission keys needed to see it. The global search
// and the Dashboard quick access both read from here, so they can never drift apart.

export const tabAction = (tab) => ({ type: 'tab', tab });

// perms: every key is required. anyOf: at least one is required.
const page = (id, label, pageId, perms, keywords = [], action = null) => ({ id, kind: 'page', label, page: pageId, action, perms, keywords });
const sub = (id, parent, label, pageId, tab, perms, keywords = []) => ({ id, kind: 'tab', parent, label, page: pageId, action: tabAction(tab), perms, keywords });
const setting = (id, label, tab, perms, keywords = [], anyOf = null) => ({ id, kind: 'settings', parent: 'Settings', label, page: 'settings', action: tabAction(tab), perms: ['settings.view', ...perms], anyOf, keywords });

export const DESTINATIONS = [
  // ── Pages & modules (the sidebar)
  page('dashboard', 'Dashboard', 'dashboard', ['dashboard.view'], ['home', 'overview']),
  page('students', 'Students', 'students', ['students.view'], ['pupils']),
  page('students-add', 'New admission', 'students', ['students.view', 'students.add'], ['admission', 'add student', 'register'], 'add'),
  page('families', 'Families', 'families', ['families.view'], ['parents', 'guardians']),
  page('attendance', 'Attendance', 'attendance', ['attendance.view'], ['present', 'absent']),
  page('complaints', 'Complaints', 'complaints', ['complaints.view'], ['tickets']),
  page('affairs', 'Student Affairs', 'affairs', ['behavior.view'], ['behaviour', 'behavior', 'incidents', 'discipline', 'assessment']),
  page('fees', 'Fees', 'fees', ['fees.view'], ['challan', 'payment']),
  page('pos', 'POS', 'pos', ['pos.view'], ['point of sale', 'canteen', 'shop']),
  page('inventory', 'Inventory', 'inventory', ['inventory.view'], ['stock', 'products']),
  page('finance', 'Finance', 'finance', ['expenses.view'], ['expenses', 'vouchers', 'accounts']),
  page('exams', 'Exams', 'exams', ['exams.view'], ['tests', 'marks', 'results']),
  page('timetable', 'Timetable', 'timetable', ['timetable.view'], ['schedule', 'periods']),
  page('diary', 'Diary', 'diary', ['diary.view'], ['homework']),
  page('staff', 'Staff', 'staff', ['staff.view'], ['teachers', 'employees']),
  page('reports', 'Reports', 'reports', ['reports.view'], ['export', 'analytics']),
  page('documents', 'Documents', 'documents', ['documents.view'], ['certificates', 'cards']),
  page('settings', 'Settings', 'settings', ['settings.view'], ['configuration']),

  // ── Fees
  sub('fees-collection', 'Fees', 'Collection', 'fees', 'collection', ['fees.view'], ['collect', 'receive', 'pay']),
  sub('fees-assignment', 'Fees', 'Fee Assignment', 'fees', 'assignment', ['fees.view', 'fees.assign'], ['assign', 'discount']),
  sub('fees-generate', 'Fees', 'Generate', 'fees', 'generate', ['fees.view', 'fees.challan.generate'], ['challans', 'generate challans']),
  sub('fees-ledger', 'Fees', 'Ledger', 'fees', 'ledger', ['fees.view'], ['statement']),
  // ── Staff
  sub('staff-list', 'Staff', 'Staff List', 'staff', 'list', ['staff.view'], ['add staff', 'directory']),
  sub('staff-assignments', 'Staff', 'Assignments', 'staff', 'assignments', ['staff.view'], ['teacher assignments', 'subjects']),
  sub('staff-payroll', 'Staff', 'Payroll', 'staff', 'payroll', ['staff.view'], ['salary', 'salaries']),
  sub('staff-attendance', 'Staff', 'Staff Attendance', 'staff', 'attendance', ['staff.view'], ['present']),
  sub('staff-advances', 'Staff', 'Advances', 'staff', 'advances', ['staff.view'], ['loans']),
  sub('staff-slips', 'Staff', 'Salary Slips', 'staff', 'slips', ['staff.view'], ['payslip']),
  // ── Inventory
  sub('inv-products', 'Inventory', 'Products', 'inventory', 'products', ['inventory.view'], ['add product', 'stock']),
  sub('inv-po', 'Inventory', 'Purchase Orders', 'inventory', 'po', ['inventory.view'], ['purchase order', 'po']),
  sub('inv-grn', 'Inventory', 'GRN', 'inventory', 'purchases', ['inventory.view'], ['goods received', 'purchases']),
  sub('inv-vendors', 'Inventory', 'Vendors', 'inventory', 'vendors', ['inventory.view'], ['suppliers']),
  sub('inv-assets', 'Inventory', 'Assets', 'inventory', 'assets', ['inventory.view'], ['equipment']),
  sub('inv-discounts', 'Inventory', 'Discounts', 'inventory', 'discounts', ['inventory.view'], []),
  // ── Finance
  sub('fin-overview', 'Finance', 'Overview', 'finance', 'overview', ['expenses.view'], []),
  sub('fin-voucher', 'Finance', 'Daily Voucher', 'finance', 'voucher', ['expenses.view'], ['add expense', 'expense', 'voucher']),
  sub('fin-ledger', 'Finance', 'Ledger', 'finance', 'ledger', ['expenses.view'], []),
  sub('fin-recurring', 'Finance', 'Recurring', 'finance', 'recurring', ['expenses.view'], ['bills']),
  sub('fin-categories', 'Finance', 'Categories', 'finance', 'categories', ['expenses.view'], []),
  sub('fin-budget', 'Finance', 'Budget', 'finance', 'budget', ['expenses.view'], []),
  sub('fin-vendors', 'Finance', 'Vendors', 'finance', 'vendors', ['expenses.view'], ['suppliers']),
  sub('fin-salary', 'Finance', 'Salary', 'finance', 'salary', ['expenses.view'], ['payroll']),
  sub('fin-reports', 'Finance', 'Reports', 'finance', 'reports', ['expenses.view'], []),
  // ── POS
  sub('pos-checkout', 'POS', 'Checkout', 'pos', 'checkout', ['pos.view'], ['sale', 'new sale']),
  sub('pos-sales', 'POS', 'Sales', 'pos', 'sales', ['pos.view'], ['transactions']),
  // ── Exams
  sub('exam-schedule', 'Exams', 'Schedule', 'exams', 'schedule', ['exams.view', 'exams.schedule.view'], []),
  sub('exam-marks', 'Exams', 'Mark Entry', 'exams', 'marks', ['exams.view', 'exams.markentry.view'], ['marks entry']),
  sub('exam-results', 'Exams', 'Results', 'exams', 'results', ['exams.view', 'exams.results.view'], []),
  sub('exam-paper', 'Exams', 'Paper Maker', 'exams', 'paper-maker', ['exams.view', 'exams.papermaker.view'], ['question paper']),
  sub('exam-datesheet', 'Exams', 'Date Sheet', 'exams', 'date-sheet', ['exams.view', 'exams.datesheet.view'], ['roll number slips']),

  // ── Settings tabs (same ids as the Settings page)
  setting('set-school', 'School Info', 'school', [], ['general', 'logo', 'principal']),
  setting('set-fees', 'Fee settings', 'fees', [], ['fee heads', 'challan design']),
  setting('set-academic', 'Academic', 'academic', [], ['classes', 'sections', 'subjects']),
  setting('set-display', 'Display & Theme', 'display', [], ['theme', 'colour', 'color', 'palette', 'dark']),
  setting('set-import', 'Import & Export', 'importexport', [], ['import', 'export', 'excel']),
  setting('set-lists', 'Lists', 'lists', [], ['religions', 'formats']),
  setting('set-bulk', 'Bulk Update', 'bulkupdate', [], ['promotion'], ['students.bulk_update', 'students.promotion']),
  setting('set-approvals', 'Approvals', 'approvals', [], []),
  setting('set-holidays', 'Calendar', 'holidays', [], ['holidays', 'events', 'off days']),
  setting('set-notifications', 'Notifications', 'notifications', ['settings.edit'], ['sms', 'whatsapp']),
  setting('set-datawipe', 'Data Reset', 'datawipe', ['settings.data_reset'], ['wipe', 'delete data']),
  setting('set-users', 'Users & Roles', 'users_roles', ['users_roles.edit'], ['permissions', 'logins', 'roles']),
  setting('set-account', 'Account', 'account', [], ['password', 'email']),
];

export const DEST_BY_ID = Object.fromEntries(DESTINATIONS.map(d => [d.id, d]));

export const canSee = (dest, hasPermission) =>
  dest.perms.every(k => hasPermission(k)) && (!dest.anyOf || dest.anyOf.some(k => hasPermission(k)));

// ── Quick access ───────────────────────────────────────────
const TONES = {
  primary: { tint: 'var(--primary-50)', accent: 'var(--primary)' },
  green:   { tint: '#EAF7F0', accent: '#15803D' },
  blue:    { tint: '#EAF4FD', accent: '#0369A1' },
  amber:   { tint: '#FFF7E6', accent: '#B45309' },
  violet:  { tint: '#F3EEFC', accent: '#7A4FD6' },
  orange:  { tint: '#FFF1EA', accent: '#C2410C' },
  rose:    { tint: '#FDEEF4', accent: '#BE185D' },
  slate:   { tint: '#EEF0F6', accent: '#475569' },
};

// `to` is a DESTINATIONS id; `extra` are permission keys on top of the destination's own.
// "Send fee reminders" is not here on purpose: the app has no page that sends reminders.
const qa = (id, label, to, Icon, tone, extra = []) => ({ id, label, to, Icon, ...TONES[tone], extra });

export const QUICK_ACTIONS = [
  qa('collect-fee', 'Collect fee', 'fees-collection', Wallet, 'primary', ['fees.challan.collect']),
  qa('generate-challans', 'Generate challans', 'fees-generate', FilePlus2, 'amber'),
  qa('print-challans', 'Print challans', 'fees-collection', Printer, 'amber', ['fees.challan.generate']),
  qa('admission', 'Admission', 'students-add', UserPlus, 'green'),
  qa('families', 'Families', 'families', House, 'rose'),
  qa('attendance', 'Attendance', 'attendance', CalendarCheck, 'blue'),
  qa('staff-attendance', 'Staff attendance', 'staff-attendance', UserCheck, 'blue'),
  qa('add-staff', 'Add staff', 'staff-list', UserRoundPlus, 'violet'),
  qa('payroll', 'Payroll', 'staff-payroll', Banknote, 'green'),
  qa('pos-sale', 'POS sale', 'pos-checkout', ShoppingCart, 'violet', ['pos.sell']),
  qa('add-product', 'Add product', 'inv-products', PackagePlus, 'slate'),
  qa('purchase-order', 'Purchase order', 'inv-po', ClipboardList, 'slate'),
  qa('grn', 'GRN', 'inv-grn', PackageCheck, 'slate'),
  qa('vendors', 'Vendors', 'inv-vendors', Truck, 'amber'),
  qa('add-expense', 'Add expense', 'fin-voucher', Receipt, 'orange'),
  qa('finance', 'Finance', 'fin-overview', Landmark, 'orange'),
  qa('marks-entry', 'Exams / marks entry', 'exam-marks', GraduationCap, 'blue'),
  qa('timetable', 'Timetable', 'timetable', CalendarClock, 'violet'),
  qa('diary', 'Diary', 'diary', BookOpen, 'green'),
  qa('complaints', 'Complaints', 'complaints', MessageSquareWarning, 'rose'),
  qa('reports', 'Reports', 'reports', BarChart3, 'blue'),
  qa('users-roles', 'Users & Roles', 'set-users', ShieldCheck, 'slate'),
  qa('settings', 'Settings', 'settings', SettingsIcon, 'slate'),
];

export const DEFAULT_QUICK_ACCESS = ['collect-fee', 'admission', 'print-challans', 'attendance', 'pos-sale', 'add-expense'];
export const MAX_QUICK_ACCESS = 6;

// Resolve a quick action to where it goes; null when it should not be offered to this user.
export function resolveQuickAction(action, hasPermission) {
  const dest = DEST_BY_ID[action.to];
  if (!dest) return null;
  if (!canSee(dest, hasPermission)) return null;
  if (!action.extra.every(k => hasPermission(k))) return null;
  return { ...action, page: dest.page, go: dest.action };
}
